import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "sonner";

import type {
  Appointment,
  Consultation,
  DetailedPrescription,
  Patient,
  TestOrder,
} from "../UserContext.types";

import {
  DEFAULT_APPOINTMENTS,
  DEFAULT_CONSULTATIONS,
  DEFAULT_PATIENTS,
  loadFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS,
} from "./storage.utils";

export const useClinicalData = (
  enabled: boolean
) => {
  const [patients, setPatients] =
    useState<Patient[]>([]);

  const [appointments, setAppointments] =
    useState<Appointment[]>([]);

  const [consultations, setConsultations] =
    useState<Consultation[]>([]);

  const [
    isLoadingPatients,
    setIsLoadingPatients,
  ] = useState(false);

  const [
    isLoadingAppointments,
    setIsLoadingAppointments,
  ] = useState(false);

  const patientsLoaded = useRef(false);
  const appointmentsLoaded = useRef(false);
  const consultationsLoaded = useRef(false);

  const fetchPatients =
    useCallback(async (): Promise<void> => {
      if (patientsLoaded.current) {
        return;
      }

      setIsLoadingPatients(true);

      try {
        const storedPatients =
          loadFromLocalStorage<Patient[]>(
            STORAGE_KEYS.PATIENTS,
            DEFAULT_PATIENTS
          );

        setPatients(storedPatients);
        patientsLoaded.current = true;
      } finally {
        setIsLoadingPatients(false);
      }
    }, []);

  const fetchAppointments =
    useCallback(async (): Promise<void> => {
      if (appointmentsLoaded.current) {
        return;
      }

      setIsLoadingAppointments(true);

      try {
        const storedAppointments =
          loadFromLocalStorage<
            Appointment[]
          >(
            STORAGE_KEYS.APPOINTMENTS,
            DEFAULT_APPOINTMENTS
          );

        setAppointments(
          storedAppointments
        );

        appointmentsLoaded.current = true;
      } finally {
        setIsLoadingAppointments(false);
      }
    }, []);

  const fetchConsultations =
    useCallback(async (): Promise<void> => {
      if (consultationsLoaded.current) {
        return;
      }

      const storedConsultations =
        loadFromLocalStorage<
          Consultation[]
        >(
          STORAGE_KEYS.CONSULTATIONS,
          DEFAULT_CONSULTATIONS
        );

      setConsultations(
        storedConsultations
      );

      consultationsLoaded.current = true;
    }, []);

  useEffect(() => {
    if (!enabled) {
      patientsLoaded.current = false;
      appointmentsLoaded.current = false;
      consultationsLoaded.current = false;
      setPatients([]);
      setAppointments([]);
      setConsultations([]);
      return;
    }

    void Promise.all([
      fetchPatients(),
      fetchAppointments(),
      fetchConsultations(),
    ]);
  }, [
    enabled,
    fetchPatients,
    fetchAppointments,
    fetchConsultations,
  ]);

  useEffect(() => {
    if (patientsLoaded.current) {
      saveToLocalStorage(
        STORAGE_KEYS.PATIENTS,
        patients
      );
    }
  }, [patients]);

  useEffect(() => {
    if (appointmentsLoaded.current) {
      saveToLocalStorage(
        STORAGE_KEYS.APPOINTMENTS,
        appointments
      );
    }
  }, [appointments]);

  useEffect(() => {
    if (consultationsLoaded.current) {
      saveToLocalStorage(
        STORAGE_KEYS.CONSULTATIONS,
        consultations
      );
    }
  }, [consultations]);

  const addPatient = useCallback(
    async (
      newPatient: Patient
    ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const updatePatient = useCallback(
    async (
      id: string,
      updates: Partial<Patient>
    ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const deletePatient = useCallback(
    async (id: string): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const addAppointment = useCallback(
    async (
      newAppointment: Appointment
    ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const updateAppointment = useCallback(
    async (
      id: string,
      updates: Partial<Appointment>
    ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const deleteAppointment = useCallback(
    async (id: string): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const updateAppointmentStatus =
    useCallback(
      async (
        id: string,
        status: Appointment["status"]
      ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      []
    );

  const addConsultation = useCallback(
    async (
      newConsultation: Consultation
    ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    []
  );

  const updateConsultation =
    useCallback(
      async (
        id: string,
        updates: Partial<Consultation>
      ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      []
    );

  const deleteConsultation =
    useCallback(
      async (
        id: string
      ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      []
    );

  const sharePrescription =
    useCallback(
      async (
        item: DetailedPrescription & {
          consultationId: string;
          patientId: string;
        }
      ): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      [patients]
    );

  const shareTestOrder = useCallback(
    async (testOrder: {
      consultationId: string;
      patientId: string;
      testName: string;
      instructions: string;
      notes?: string;
    }): Promise<void> => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [patients]
  );

  const refreshData =
    useCallback(async (): Promise<void> => {
      setIsLoadingPatients(true);
      setIsLoadingAppointments(true);

      try {
        const refreshedPatients =
          loadFromLocalStorage<Patient[]>(
            STORAGE_KEYS.PATIENTS,
            DEFAULT_PATIENTS
          );

        const refreshedAppointments =
          loadFromLocalStorage<
            Appointment[]
          >(
            STORAGE_KEYS.APPOINTMENTS,
            DEFAULT_APPOINTMENTS
          );

        const refreshedConsultations =
          loadFromLocalStorage<
            Consultation[]
          >(
            STORAGE_KEYS.CONSULTATIONS,
            DEFAULT_CONSULTATIONS
          );

        patientsLoaded.current = true;
        appointmentsLoaded.current = true;
        consultationsLoaded.current = true;

        setPatients(refreshedPatients);

        setAppointments(
          refreshedAppointments
        );

        setConsultations(
          refreshedConsultations
        );

        toast.success("Data refreshed");
      } finally {
        setIsLoadingPatients(false);
        setIsLoadingAppointments(false);
      }
    }, []);

  const clearAllData =
    useCallback((): void => {
      patientsLoaded.current = false;
      appointmentsLoaded.current = false;
      consultationsLoaded.current = false;

      setPatients([]);
      setAppointments([]);
      setConsultations([]);

      removeFromLocalStorage(
        STORAGE_KEYS.PATIENTS
      );

      removeFromLocalStorage(
        STORAGE_KEYS.APPOINTMENTS
      );

      removeFromLocalStorage(
        STORAGE_KEYS.CONSULTATIONS
      );

      toast.success("All data cleared");
    }, []);

  const resetToDefaults =
    useCallback((): void => {
      patientsLoaded.current = true;
      appointmentsLoaded.current = true;
      consultationsLoaded.current = true;

      setPatients([...DEFAULT_PATIENTS]);

      setAppointments([
        ...DEFAULT_APPOINTMENTS,
      ]);

      setConsultations([
        ...DEFAULT_CONSULTATIONS,
      ]);

      saveToLocalStorage(
        STORAGE_KEYS.PATIENTS,
        DEFAULT_PATIENTS
      );

      saveToLocalStorage(
        STORAGE_KEYS.APPOINTMENTS,
        DEFAULT_APPOINTMENTS
      );

      saveToLocalStorage(
        STORAGE_KEYS.CONSULTATIONS,
        DEFAULT_CONSULTATIONS
      );

      toast.success(
        "Data reset to defaults"
      );
    }, []);

  const resetClinicalState =
    useCallback((): void => {
      patientsLoaded.current = false;
      appointmentsLoaded.current = false;
      consultationsLoaded.current = false;

      setPatients([]);
      setAppointments([]);
      setConsultations([]);
    }, []);

  return {
    patients,
    appointments,
    consultations,

    isLoadingPatients,
    isLoadingAppointments,

    fetchPatients,
    fetchAppointments,
    fetchConsultations,

    addPatient,
    updatePatient,
    deletePatient,

    addAppointment,
    updateAppointment,
    deleteAppointment,
    updateAppointmentStatus,

    addConsultation,
    updateConsultation,
    deleteConsultation,

    sharePrescription,
    shareTestOrder,

    refreshData,
    clearAllData,
    resetToDefaults,
    resetClinicalState,
  };
};
